import { type Key } from 'react'

import { type ShipmentOrdersListExEntity } from '@/libs/api'

/**
 * AntD Table の rowSelection.onChange(selectedRowKeys, selectedRows) の結果を、
 * 選択順を保った entity 配列に変換する
 *
 * `preserveSelectedRowKeys: true` の AntD は、現在の dataSource に存在しないキーの
 * record を内部キャッシュ (preserveRecordsRef) から `keys.map(key => preserveRecordsRef.current.get(key))`
 * で引くだけで filter しないため、rows の要素は undefined になり得る (antd 5.17.4
 * node_modules/antd/es/table/hooks/useSelection.js で確認済み)。undefined の場合は
 * 直前の選択 (prev) から id 一致で補完し、それでも見つからなければ配列に undefined を
 * 混入させないようその要素を落とす。
 */
export const mergeSelectedOrders = (
  prev: ShipmentOrdersListExEntity[],
  keys: Key[],
  rows: (ShipmentOrdersListExEntity | undefined)[]
): ShipmentOrdersListExEntity[] =>
  keys.reduce<ShipmentOrdersListExEntity[]>((merged, key, index) => {
    // keys は React.Key (number | string) で来るため id (number) と突合するには正規化が必要
    const order =
      rows[index] ?? prev.find((candidate) => candidate.id === Number(key))
    if (order) {
      merged.push(order)
    }
    return merged
  }, [])

/**
 * 選択行のうち現在のページ結果 (pageRows) に含まれないものをピン留め対象として分離する
 *
 * サーバーサイドのページング・ソート・検索絞り込みで選択済み行が画面から消えないよう、
 * テーブル先頭にピン留め表示するための dataSource を組み立てる。rowKey="id" が
 * pinnedOrders と pageRows で重複すると React key 警告とチェックボックス二重描画が
 * 起きるため、id 集合で除外して重複を防ぐ。
 */
export const splitPinnedOrders = (
  selected: ShipmentOrdersListExEntity[],
  pageRows: ShipmentOrdersListExEntity[]
): {
  pinnedOrders: ShipmentOrdersListExEntity[]
  dataSource: ShipmentOrdersListExEntity[]
} => {
  const pageRowIds = new Set(pageRows.map((row) => row.id))
  // 選択順を維持するため filter で除外する (順序を崩す Set 経由の再構築はしない)
  const pinnedOrders = selected.filter((order) => !pageRowIds.has(order.id))

  return {
    pinnedOrders,
    // pinned が無い場合は pageRows の参照をそのまま返し、不要な再レンダーを避ける
    dataSource:
      pinnedOrders.length === 0 ? pageRows : [...pinnedOrders, ...pageRows]
  }
}

/** まとめキー左のチェックに表示する、束ね単位の選択状態 */
export type BundleSelectionState = 'none' | 'partial' | 'all'

/**
 * 表示中の行 (rows) を束ね単位に集計し、束ねごとの選択状態を返す
 *
 * 選択済みで現ページに無い行はピン留めで rows に必ず含まれる (splitPinnedOrders) ため、
 * 「画面から見えている範囲のメンバー」は rows だけで揃う。一覧にも選択にも現れていない
 * メンバーはここでは分からないので、束ね全件はチェック押下時に bundleId で引き直す。
 */
export const buildBundleSelectionStates = (
  rows: ShipmentOrdersListExEntity[],
  selectedIds: Set<number>
): Map<number, BundleSelectionState> => {
  const counts = new Map<number, { total: number; selected: number }>()

  rows.forEach((row) => {
    if (row.bundleId === null) return
    const count = counts.get(row.bundleId) ?? { total: 0, selected: 0 }
    count.total += 1
    if (selectedIds.has(row.id)) {
      count.selected += 1
    }
    counts.set(row.bundleId, count)
  })

  return new Map(
    [...counts].map(([bundleId, { total, selected }]) => [
      bundleId,
      selected === 0 ? 'none' : selected === total ? 'all' : 'partial'
    ])
  )
}

/**
 * 選択に依頼を追加する
 *
 * 既に選択済みの依頼は選択順を保つため元の位置に残し、未選択のものだけを末尾に足す。
 * 追加が1件も無い場合は selected の参照をそのまま返す（不要な再レンダーを避ける）。
 */
export const addOrdersToSelection = (
  selected: ShipmentOrdersListExEntity[],
  orders: ShipmentOrdersListExEntity[]
): ShipmentOrdersListExEntity[] => {
  const selectedIds = new Set(selected.map((order) => order.id))
  const added = orders.filter((order) => !selectedIds.has(order.id))

  return added.length === 0 ? selected : [...selected, ...added]
}

/**
 * 指定した束ねに属する依頼を選択から取り除く
 *
 * 除外が1件も無い場合は selected の参照をそのまま返す。
 */
export const removeBundleFromSelection = (
  selected: ShipmentOrdersListExEntity[],
  bundleId: number
): ShipmentOrdersListExEntity[] => {
  const remained = selected.filter((order) => order.bundleId !== bundleId)

  return remained.length === selected.length ? selected : remained
}

/**
 * サーバー再取得 (router.invalidate() 等) 後に、選択行キャッシュの鮮度を補正する
 *
 * 選択中の各行について、freshPageRows に同じ id があればそちらの entity に差し替える。
 * 表示条件外に落ちて freshPageRows に含まれない行はスナップショットのまま据え置く。
 * 差し替えが1件も発生しなかった場合は selected の参照をそのまま返す。useEffect 内で
 * setState する用途で使うため、新しい配列を毎回返すと state が変わり続け無限ループに
 * なることを防ぐために必須の挙動。
 */
export const refreshSelectedOrders = (
  selected: ShipmentOrdersListExEntity[],
  freshPageRows: ShipmentOrdersListExEntity[]
): ShipmentOrdersListExEntity[] => {
  const freshOrderById = new Map(freshPageRows.map((row) => [row.id, row]))
  let hasReplaced = false

  const refreshed = selected.map((order) => {
    const fresh = freshOrderById.get(order.id)
    if (fresh && fresh !== order) {
      hasReplaced = true
      return fresh
    }
    return order
  })

  return hasReplaced ? refreshed : selected
}
